import { Skeleton } from '@mui/material'
import { useGridApiRef } from '@mui/x-data-grid-premium'
import cn from 'classnames'
import Button from 'ext-ui/components/Button/Button'
import InlineConfirmationPanel from 'ext-ui/components/InlineConfirmationPanel/InlineConfirmationPanel'
import Pill from 'ext-ui/components/Pill/Pill'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import LockedIcon from 'ext-ui/svg/icon/LockedIcon'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { DropdownValue } from 'src/components/CreditCardDropdownV2/CreditCardDropdownV2'
import SvgSplit from 'src/components/Svgs/SvgSplit'
import SvgUnsplit from 'src/components/Svgs/SvgUnsplit'
import TransactionExpenseDetailsFormV2 from 'src/components/TransactionExpenseDetailsFormV2/TransactionExpenseDetailsFormV2'
import SplitExpenseCategoriesDataGrid from 'src/components/TransactionsDataGrid/SplitExpenseCategoriesDataGrid'
import { TransactionExpenseCategorySplit } from 'src/components/TransactionsDataGrid/transactionsDataGrid.types'
import { useTranslate } from 'src/localization/Trns'
import {
  ConnectedPlatformStatus,
  TransactionDetailModel,
  TransactionDetailResponse,
  TransactionStatus,
} from 'src/models/transaction.models'
import { useQueryActiveTransactionIntegration } from 'src/queries/integrations/orgIntegrations.queries'
import {
  activeAlphabeticalExpenseCategoryFiltersRequestParams,
  ExpenseCategoryType,
  useQueryExpenseCategories,
} from 'src/queries/organizations/expenseCategories.queries'
import { useHasSaasFeatures } from 'src/queries/organizations/saas.queries'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { css } from 'styled-components'

import style, {
  skeletonCategory,
  skeletonLabel,
  skeletonTitle,
} from './ExpenseDetailsSection.style'

interface ExpenseDetailsSectionProps {
  transaction: TransactionDetailModel
  splits: TransactionExpenseCategorySplit[]
  onChange: (splits: TransactionExpenseCategorySplit[]) => void
  onSaveSplits?: (
    splits: TransactionExpenseCategorySplit[],
  ) => Promise<TransactionDetailResponse | void>
  splitError?: string
}

const ExpenseDetailsSection: React.FC<ExpenseDetailsSectionProps> = ({
  transaction,
  splits,
  onChange,
  onSaveSplits,
  splitError,
}): React.ReactElement => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )

  const showTransactionSplits = useHasSaasFeatures()
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const { data: activeTransactionIntegration } =
    useQueryActiveTransactionIntegration(transaction.id)
  const { data: expCatSchema, isPending: expCatSchemaLoading } =
    useQueryExpenseCategories({
      filters: activeAlphabeticalExpenseCategoryFiltersRequestParams,
    })

  const [showSplit, setShowSplit] = useState(
    showTransactionSplits && (transaction.isSplit ?? false),
  )
  const [confirmUnsplitting, setConfirmUnsplitting] = useState(false)
  const [initialSplitsSignature, setInitialSplitsSignature] = useState<
    string | null
  >(null)
  const [localSplitError, setLocalSplitError] = useState<string>()

  const apiRef = useGridApiRef()

  const isSplitLocked =
    activeTransactionIntegration?.syncStatus === ConnectedPlatformStatus.Success

  const computeSplitSignature = useCallback(
    (targetSplits: TransactionExpenseCategorySplit[]): string => {
      return JSON.stringify(
        targetSplits.map((split) => {
          const splitValue =
            split?.splitValue === undefined || split?.splitValue === null
              ? ''
              : String(split.splitValue)
          const expenseCategories = split?.expenseCategories ?? {}
          const normalizedCategories = Object.keys(expenseCategories)
            .sort()
            .map((categoryId) => {
              const mapping = expenseCategories[categoryId]
              return {
                categoryId,
                labelId: mapping?.labelId ?? '',
                labelName: mapping?.labelName ?? '',
              }
            })
          return {
            splitValue,
            expenseCategories: normalizedCategories,
          }
        }),
      )
    },
    [],
  )

  const currentSplitsSignature = useMemo(
    () => computeSplitSignature(splits),
    [computeSplitSignature, splits],
  )

  const hasSplitChanges = useMemo(() => {
    if (!showSplit) {
      return false
    }
    if (initialSplitsSignature === null) {
      return false
    }
    return currentSplitsSignature !== initialSplitsSignature
  }, [currentSplitsSignature, initialSplitsSignature, showSplit])

  useEffect(() => {
    if (!showTransactionSplits) {
      setShowSplit(false)
      setConfirmUnsplitting(false)
      return
    }
    setShowSplit(transaction.isSplit ?? false)
  }, [showTransactionSplits, transaction.id, transaction.isSplit])

  useEffect(() => {
    if (!showSplit) {
      setInitialSplitsSignature(null)
      return
    }

    setInitialSplitsSignature((prev) => {
      if (prev !== null) {
        return prev
      }
      if (splits.length === 0) {
        return prev
      }
      return computeSplitSignature(splits)
    })
  }, [computeSplitSignature, showSplit, splits])

  const [saving, setSaving] = useState(false)
  const getSplitAmountValue = useCallback(
    (split: TransactionExpenseCategorySplit): number => {
      const value = split.splitValue
      if (value === undefined || value === null) return 0
      const numeric =
        typeof value === 'number' ? value : Number.parseFloat(String(value))
      return Number.isFinite(numeric) ? numeric : 0
    },
    [],
  )
  const hasValidSplitAmount = useCallback(
    (split: TransactionExpenseCategorySplit): boolean =>
      getSplitAmountValue(split) > 0,
    [getSplitAmountValue],
  )
  const hasExpenseCategorySelection = useCallback(
    (split: TransactionExpenseCategorySplit): boolean => {
      return Object.values(split.expenseCategories ?? {}).some((mapping) => {
        const candidate = mapping.labelId ?? mapping.labelName
        return candidate !== undefined && candidate !== null
          ? String(candidate).trim() !== ''
          : false
      })
    },
    [],
  )
  const hasIncompleteSplit = useCallback(
    (targetSplits: TransactionExpenseCategorySplit[]): boolean =>
      targetSplits.some(
        (split) =>
          !hasValidSplitAmount(split) || !hasExpenseCategorySelection(split),
      ),
    [hasExpenseCategorySelection, hasValidSplitAmount],
  )
  const handleSaveSplitChanges = useCallback(
    async (nextSplits: TransactionExpenseCategorySplit[]) => {
      if (hasIncompleteSplit(nextSplits)) {
        setLocalSplitError(
          translate(
            'One or more of the splits above is incomplete. You must include an amount and at least one expense category selection.',
          ),
        )
        return
      }
      setLocalSplitError(undefined)
      setSaving(true)
      try {
        const response = await onSaveSplits?.(nextSplits)
        if (response?.isSplit === false) {
          setShowSplit(false)
          return
        }
        setInitialSplitsSignature(computeSplitSignature(nextSplits))
      } catch (error) {
        // Swallow to avoid unhandled promise rejections; errors surface via splitError.
      } finally {
        setSaving(false)
      }
    },
    [computeSplitSignature, hasIncompleteSplit, onSaveSplits],
  )
  useEffect(() => {
    if (!localSplitError) return
    if (!hasIncompleteSplit(splits)) {
      setLocalSplitError(undefined)
    }
  }, [hasIncompleteSplit, localSplitError, splits])

  const onSuccessfulSubmit = (
    categoryId: string,
    selectedValue: DropdownValue | undefined,
  ): void => {
    analytics?.track(
      'transaction detail - edit transaction vendor and expense categories - button press',
      {
        intent: 'edit transaction vendor and expense categories',
        interaction: 'button press',
        screen: calculatedBackgroundLocation.pathname,
        expenseDetails: [
          {
            categoryId,
            labelId: selectedValue?.value ?? '',
          },
        ],
      },
    )
  }

  if (expCatSchemaLoading) {
    return (
      <div className={'flex-column gap-5'}>
        <Skeleton variant={'rounded'} css={skeletonTitle} />
        {[1, 2, 3].map((_, idx) => (
          <div key={idx} className={'flex-column gap-2'}>
            <Skeleton variant={'rounded'} css={skeletonCategory} />
            <Skeleton variant={'rounded'} css={skeletonLabel} />
          </div>
        ))}
      </div>
    )
  }

  if (expCatSchema === undefined || expCatSchema.length === 0) {
    return <></>
  }

  const isMissingRequiredExpenseCategories = useMemo(() => {
    if (isSplitLocked) {
      return false
    }
    if (!transaction?.isCleared) {
      return false
    }
    const requiredExtCats = expCatSchema.filter(
      (cat) =>
        cat.requiredForTransactions &&
        ![ExpenseCategoryType.Vendor, ExpenseCategoryType.Customer].includes(
          cat.type,
        ),
    )

    // Customer and Vendor need special treatment due to not having categoryIds
    const customer = expCatSchema.find(
      (cat) => cat.type === ExpenseCategoryType.Customer,
    )
    const customerRequired = customer?.requiredForTransactions ?? false
    const customerMissing =
      customerRequired &&
      transaction?.expenseDetails?.find(
        (expDet) => expDet.categoryType === ExpenseCategoryType.Customer,
      ) === undefined
    const vendor = expCatSchema.find(
      (cat) => cat.type === ExpenseCategoryType.Vendor,
    )
    const vendorRequired = vendor?.requiredForTransactions ?? false
    const vendorMissing =
      vendorRequired &&
      transaction?.expenseDetails?.find(
        (expDet) => expDet.categoryType === ExpenseCategoryType.Vendor,
      ) === undefined

    return (
      customerMissing ||
      vendorMissing ||
      requiredExtCats.some((reqCat) => {
        const expDetail = transaction?.expenseDetails?.find(
          (expDet) => expDet.categoryId === reqCat.id,
        )
        // Checking labelName to accomodate for free text
        return expDetail === undefined || expDetail.labelName === undefined
      })
    )
  }, [expCatSchema, isSplitLocked, transaction?.expenseDetails])

  return (
    <div data-testid={'expense-categories-box'} css={style}>
      <div className={'header'}>
        {activeTransactionIntegration?.syncStatus ===
          ConnectedPlatformStatus.Success && (
          <TooltipIcon icon={<LockedIcon />} placement={'top'}>
            <div className={'flex-column gap-4'}>
              <p>
                {translate(
                  'Expense Categories for this transaction have been synced to ${orgLvlIntegration} and are uneditable.',
                  {
                    orgLvlIntegration: activeIntegration?.platformName || '',
                  },
                )}
              </p>
              <p>
                {translate(
                  'Once synced, these fields can only be updated in ${orgLvlIntegration}.',
                  {
                    orgLvlIntegration: activeIntegration?.platformName || '',
                  },
                )}
              </p>
            </div>
          </TooltipIcon>
        )}
        <span className={'font-h3'}>{translate('Expense Categories')}</span>
        {isMissingRequiredExpenseCategories && (
          <Pill size={'small'} color={'severe'}>
            {translate('Missing')}
          </Pill>
        )}
        {showTransactionSplits && !isSplitLocked && (
          <div
            css={css`
              margin-left: auto;
            `}
          >
            <Button
              className={cn('split-button', { selected: showSplit })}
              variant={'secondary'}
              dropShadow={false}
              disabled={transaction.status === TransactionStatus.Pending}
              disabledTooltip={translate(
                'Pending transactions cannot be split',
              )}
              onClick={(): void => {
                if (showSplit) {
                  if (transaction.isSplit) {
                    setConfirmUnsplitting(true)
                    return
                  }
                  setShowSplit(false)
                  return
                }
                setShowSplit(true)
              }}
            >
              {showSplit ? <SvgUnsplit /> : <SvgSplit />}
              <span>
                {showSplit ? translate('Undo Split') : translate('Split')}
              </span>
            </Button>
          </div>
        )}
      </div>
      {confirmUnsplitting && transaction.isSplit && (
        <InlineConfirmationPanel
          message={translate(
            'Are you sure you want to unsplit this transaction? You will lose your splits and need to recode them if you decide to split again.',
          )}
          confirmLabel={translate('Confirm Undo')}
          onCancel={() => setConfirmUnsplitting(false)}
          onConfirm={() => {
            setConfirmUnsplitting(false)
            setShowSplit(false)
            onChange([])
            onSaveSplits?.([])
          }}
        />
      )}
      {showTransactionSplits && showSplit && (
        <>
          <SplitExpenseCategoriesDataGrid
            apiRef={apiRef}
            transactionId={transaction.id}
            splits={splits}
            onChange={onChange}
            totalAmountCents={transaction.clearingOrAuthAmountCents}
            onSave={handleSaveSplitChanges}
            splitError={localSplitError ?? splitError}
            saving={saving}
            disableSave={!hasSplitChanges || splits.length < 2}
            isLocked={isSplitLocked}
          />
        </>
      )}
      {(!showTransactionSplits || !showSplit) && (
        <TransactionExpenseDetailsFormV2
          expCatSchema={expCatSchema}
          transactionId={transaction?.id}
          transactionIsCleared={transaction.isCleared}
          expenseDetails={transaction?.expenseDetails}
          onExpenseCategorySelectSuccess={onSuccessfulSubmit}
        />
      )}
    </div>
  )
}

export default ExpenseDetailsSection
